/**
 * ENTRY POINT
 *
 * In HTML, load only this file:
 *   <script type="module" src="js/app.js"></script>
 *
 * Add to HTML <head> for map (choose one):
 *   Neshan:  <script src="https://static.neshan.org/sdk/mapboxgl/v1.3.2/neshan-mapboxgl.js"></script>
 *   Balad:   <script src="https://balad.ir/api/mapkit/v4/mapkit.js" data-balad-token="YOUR_KEY"></script>
 */ /**
 * MODEL — Application state, persistence, and business logic.
 * No DOM. No API calls except validateCoupon (pure business logic dependency).
 */ /**
 * CONFIG — Single source of truth for all app-wide configuration.
 */ const $9dfdde2870d5f6f9$export$1630b5343357e8b5 = {
    API_BASE_URL: 'https://fastfood-api.emran-h5124.workers.dev/api',
    STORAGE_KEY: 'SAFRAN_APP_STATE',
    IMAGE_CACHE_KEY: 'SAFRAN_IMG_CACHE',
    OTP_EXPIRY_MS: 600000,
    OTP_COUNTDOWN_SEC: 60,
    // ── Navigation guards ────────────────────────────────────────────────────
    // Pages that require a non-empty cart to enter
    REQUIRES_CART: [
        'invoice',
        'otp',
        'checkout'
    ],
    // Pages that require authentication to enter
    REQUIRES_AUTH: [
        'checkout'
    ],
    // Pages that require a completed order to enter
    REQUIRES_ORDER: [
        'success'
    ],
    // Pages blocked when restaurant is closed
    BLOCKED_WHEN_CLOSED: [
        'checkout'
    ],
    PAYMENT_GATEWAY: {
        provider: 'zarinpal',
        merchantId: 'YOUR_MERCHANT_ID',
        callbackUrl: `${window.location.origin}/#payment-result`,
        sandbox: true
    },
    SMS_PROVIDER: {
        provider: 'kavenegar',
        apiKey: 'YOUR_API_KEY',
        templateName: 'verify'
    },
    // ── Map providers ────────────────────────────────────────────────────────
    // Switch 'provider' to 'neshan' or 'balad' and fill the key
    MAP: {
        provider: 'neshan',
        apiKey: 'YOUR_MAP_API_KEY',
        defaultLat: 35.6892,
        defaultLng: 51.3890,
        defaultZoom: 14
    }
};


/**
 * UTILS — Shared pure utility functions. No side effects, no DOM, no state.
 */ function $08fa37fff71de28c$export$c86caf1be068fb8d(number) {
    if (typeof number !== 'number') return "\u06F0 \u062A\u0648\u0645\u0627\u0646";
    return number.toLocaleString('fa-IR') + " \u062A\u0648\u0645\u0627\u0646";
}
function $08fa37fff71de28c$export$4cf11838cdc2a8a8(str) {
    if (!str) return '';
    return str.replace(/[&<>'"]/g, (tag)=>({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            "'": '&#39;',
            '"': '&quot;'
        })[tag] || tag);
}
function $08fa37fff71de28c$export$1391212d75b2ee65(ms) {
    return new Promise((resolve)=>setTimeout(resolve, ms));
}
const $08fa37fff71de28c$export$51adf995cb4f5799 = {
    _key: 'SAFRAN_IMG_CACHE',
    async preload (urls) {
        const stored = this._load();
        const missing = urls.filter((u)=>u && !stored[u]);
        if (missing.length === 0) return;
        await Promise.allSettled(missing.map((url)=>this._fetchAndStore(url, stored)));
        this._save(stored);
    },
    async _fetchAndStore (url, cache) {
        try {
            const res = await fetch(url);
            if (!res.ok) return;
            const blob = await res.blob();
            await new Promise((resolve, reject)=>{
                const reader = new FileReader();
                reader.onload = ()=>{
                    cache[url] = reader.result;
                    resolve();
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (_) {}
    },
    get (url) {
        if (!url) return null;
        return this._load()[url] || null;
    },
    _load () {
        try {
            return JSON.parse(localStorage.getItem(this._key) || '{}');
        } catch (_) {
            return {};
        }
    },
    _save (cache) {
        try {
            // Keep localStorage under ~4MB: evict oldest entries if needed
            let json = JSON.stringify(cache);
            while(json.length > 4194304){
                const firstKey = Object.keys(cache)[0];
                if (!firstKey) break;
                delete cache[firstKey];
                json = JSON.stringify(cache);
            }
            localStorage.setItem(this._key, json);
        } catch (_) {}
    }
};


/**
 * API SERVICE — All backend communication.
 * Mocks are clearly marked. To go live: remove mock block, uncomment real call.
 */ 

// ─── Auth token helper ────────────────────────────────────────────────────────
function $73936c209f170d43$var$authHeader() {
    const token = localStorage.getItem('SAFRAN_AUTH_TOKEN');
    return token ? {
        Authorization: `Bearer ${token}`
    } : {};
}
function $73936c209f170d43$export$894225851aaae84a(token) {
    if (token) localStorage.setItem('SAFRAN_AUTH_TOKEN', token);
}
function $73936c209f170d43$export$df5ba283a82ad80a() {
    localStorage.removeItem('SAFRAN_AUTH_TOKEN');
}
// ─── Generic fetch wrapper ────────────────────────────────────────────────────
async function $73936c209f170d43$var$request(path, options = {}) {
    const url = `${(0, $9dfdde2870d5f6f9$export$1630b5343357e8b5).API_BASE_URL}${path}`;
    const res = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        },
        ...options
    });
    if (!res.ok) {
        let message = `\u{62E}\u{637}\u{627}\u{6CC} \u{633}\u{631}\u{648}\u{631}: ${res.status}`;
        try {
            const b = await res.json();
            message = b.message || b.error || message;
        } catch (_) {}
        throw new Error(message);
    }
    return res.json();
}
function $73936c209f170d43$export$f2449340a296d5b0(onUpdate) {
    // ── MOCK — no-op, returns immediate cleanup ──────────────────────────────
    return ()=>{};
// ── END MOCK ─────────────────────────────────────────────────────────────
// Real SSE implementation:
// const es = new EventSource(`${CONFIG.API_BASE_URL}/status/stream`);
// es.onmessage = e => { try { onUpdate(JSON.parse(e.data)); } catch (_) {} };
// es.onerror   = () => es.close();
// return () => es.close();
// Real WebSocket implementation:
// const ws = new WebSocket(`wss://your-api.com/status`);
// ws.onmessage = e => { try { onUpdate(JSON.parse(e.data)); } catch (_) {} };
// return () => ws.close();
}
async function $73936c209f170d43$export$49ee515c521055ac() {
    return $73936c209f170d43$var$request('/menu');
}
async function $73936c209f170d43$export$ef5937a3f5701761(code) {
    // ── MOCK ──────────────────────────────────────────────────────────────────
    const MOCK = {
        'SAFRAN10': {
            type: 'percent',
            value: 10,
            minOrder: 150000
        },
        'WELCOME50': {
            type: 'flat',
            value: 50000,
            minOrder: 200000
        }
    };
    await (0, $08fa37fff71de28c$export$1391212d75b2ee65)(400);
    return MOCK[code] || null;
// ── END MOCK ──────────────────────────────────────────────────────────────
// const data = await request('/coupons/validate', {
//   method: 'POST',
//   body: JSON.stringify({ code }),
// });
// return data.coupon || null;
}
async function $73936c209f170d43$export$477887879f18714(phone) {
    if (!/^09\d{9}$/.test(phone)) throw new Error("\u0641\u0631\u0645\u062A \u0634\u0645\u0627\u0631\u0647 \u0645\u0648\u0628\u0627\u06CC\u0644 \u0646\u0627\u0645\u0639\u062A\u0628\u0631 \u0627\u0633\u062A. \u0641\u0631\u0645\u062A \u0635\u062D\u06CC\u062D: 09XXXXXXXXX");
    // ── MOCK ──────────────────────────────────────────────────────────────────
    await (0, $08fa37fff71de28c$export$1391212d75b2ee65)(800);
    return {
        success: true
    };
// ── END MOCK ──────────────────────────────────────────────────────────────
// return request('/auth/send-otp', { method: 'POST', body: JSON.stringify({ phone }) });
}
async function $73936c209f170d43$export$5a44d9712f68b0dd(phone, code) {
    // ── MOCK ──────────────────────────────────────────────────────────────────
    await (0, $08fa37fff71de28c$export$1391212d75b2ee65)(800);
    if (code === '1234') return {
        success: true,
        token: 'jwt_mock_token'
    };
    throw new Error("\u06A9\u062F \u062A\u0627\u06CC\u06CC\u062F \u0646\u0627\u0645\u0639\u062A\u0628\u0631 \u0627\u0633\u062A. (\u0628\u0631\u0627\u06CC \u0648\u0631\u0648\u062F \u0622\u0632\u0645\u0627\u06CC\u0634\u06CC \u0627\u0632 \u06F1\u06F2\u06F3\u06F4 \u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u06A9\u0646\u06CC\u062F)");
// ── END MOCK ──────────────────────────────────────────────────────────────
// return request('/auth/verify-otp', { method: 'POST', body: JSON.stringify({ phone, code }) });
}
async function $73936c209f170d43$export$b1a61f48eea455bf(payload) {
    // ── MOCK ──────────────────────────────────────────────────────────────────
    await (0, $08fa37fff71de28c$export$1391212d75b2ee65)(1500);
    return {
        success: true,
        orderId: 'SAFRAN-' + Math.floor(100000 + Math.random() * 900000),
        estimatedMinutes: 45
    };
// ── END MOCK ──────────────────────────────────────────────────────────────
// return request('/orders', {
//   method:  'POST',
//   headers: authHeader(),
//   body:    JSON.stringify(payload),
// });
}
async function $73936c209f170d43$export$dcd88816af569b61({ orderId: orderId, authority: authority }) {
    // ── MOCK ──────────────────────────────────────────────────────────────────
    await (0, $08fa37fff71de28c$export$1391212d75b2ee65)(800);
    return {
        success: true,
        orderId: orderId,
        refId: 'REF-' + Math.floor(Math.random() * 1e8),
        estimatedMinutes: 45
    };
// ── END MOCK ──────────────────────────────────────────────────────────────
// return request('/payment/verify', {
//   method:  'POST',
//   headers: authHeader(),
//   body:    JSON.stringify({ orderId, authority }),
// });
}


// ─── Default State ────────────────────────────────────────────────────────────
const $f381a3c3b9cf5311$var$DEFAULT_STATE = {
    currentPage: 'menu',
    cart: [],
    orderType: 'delivery',
    orderNotes: '',
    couponCode: '',
    appliedCoupon: null,
    // Delivery location (set by map picker)
    selectedLocation: null,
    // Auth (two concerns):
    otpPhone: null,
    otpCodeSentAt: null,
    otpVerified: false,
    isAuthenticated: false,
    authToken: null,
    authenticatedPhone: null,
    receiverName: '',
    receiverPhone: '',
    receiverAddress: '',
    lastSubmittedOrder: null,
    pendingOrder: null,
    restaurantConfig: {
        isOpen: true,
        openTime: "\u06F1\u06F1:\u06F0\u06F0 \u062A\u0627 \u06F2\u06F3:\u06F0\u06F0",
        defaultDeliveryFee: 25000,
        deliveryFeeThreshold: 300000
    },
    // Transient — never persisted
    menuData: null,
    menuLoading: false,
    menuError: null
};
class $f381a3c3b9cf5311$export$75ea5ef3820854ae {
    #state = {
        ...$f381a3c3b9cf5311$var$DEFAULT_STATE
    };
    #listeners = new Set();
    getState() {
        return Object.freeze({
            ...this.#state
        });
    }
    setState(patch) {
        this.#state = {
            ...this.#state,
            ...patch
        };
        this.#persist();
        this.#notify();
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return ()=>this.#listeners.delete(listener);
    }
    #notify() {
        const snap = this.getState();
        this.#listeners.forEach((fn)=>fn(snap));
    }
    // ── Persistence ───────────────────────────────────────────────────────────
    #persist() {
        const s = this.#state;
        try {
            localStorage.setItem((0, $9dfdde2870d5f6f9$export$1630b5343357e8b5).STORAGE_KEY, JSON.stringify({
                currentPage: s.currentPage,
                cart: s.cart,
                orderType: s.orderType,
                orderNotes: s.orderNotes,
                couponCode: s.couponCode,
                appliedCoupon: s.appliedCoupon,
                selectedLocation: s.selectedLocation,
                otpPhone: s.otpPhone,
                otpCodeSentAt: s.otpCodeSentAt,
                otpVerified: s.otpVerified,
                isAuthenticated: s.isAuthenticated,
                authToken: s.authToken,
                authenticatedPhone: s.authenticatedPhone,
                receiverName: s.receiverName,
                receiverPhone: s.receiverPhone,
                receiverAddress: s.receiverAddress,
                lastSubmittedOrder: s.lastSubmittedOrder,
                pendingOrder: s.pendingOrder
            }));
        } catch (err) {
            console.warn('[Model] Persist failed:', err);
        }
    }
    restore() {
        try {
            const raw = localStorage.getItem((0, $9dfdde2870d5f6f9$export$1630b5343357e8b5).STORAGE_KEY);
            if (!raw) return;
            const saved = JSON.parse(raw);
            // Expire only the active OTP flow — never touch isAuthenticated
            if (saved.otpCodeSentAt && Date.now() - saved.otpCodeSentAt > (0, $9dfdde2870d5f6f9$export$1630b5343357e8b5).OTP_EXPIRY_MS) {
                saved.otpPhone = null;
                saved.otpCodeSentAt = null;
                saved.otpVerified = false;
            }
            const { menuData: menuData, menuLoading: menuLoading, menuError: menuError, ...safe } = saved;
            this.#state = {
                ...$f381a3c3b9cf5311$var$DEFAULT_STATE,
                ...safe
            };
        } catch (err) {
            console.error('[Model] Restore failed:', err);
        }
    }
    logout() {
        this.setState({
            isAuthenticated: false,
            authToken: null,
            authenticatedPhone: null,
            otpPhone: null,
            otpCodeSentAt: null,
            otpVerified: false
        });
    }
    // ── Navigation Guards ─────────────────────────────────────────────────────
    /**
   * Returns null if navigation is allowed, or { redirect, reason } if blocked.
   * Presenter calls this before every navigate().
   */ canNavigateTo(page) {
        const s = this.#state;
        if (page === 'success' && !s.lastSubmittedOrder) return {
            redirect: 'menu',
            reason: null
        };
        if (page === 'payment-result') return null; // always allowed — gateway callback
        if (page === 'payment-failed') return null;
        if ((page === 'invoice' || page === 'otp' || page === 'checkout') && s.cart.length === 0) return {
            redirect: 'menu',
            reason: "\u0633\u0628\u062F \u062E\u0631\u06CC\u062F \u0634\u0645\u0627 \u062E\u0627\u0644\u06CC \u0627\u0633\u062A."
        };
        if (page === 'checkout' && !s.isAuthenticated) return {
            redirect: 'otp',
            reason: "\u0627\u0628\u062A\u062F\u0627 \u0628\u0627\u06CC\u062F \u0647\u0648\u06CC\u062A \u0634\u0645\u0627 \u062A\u0627\u06CC\u06CC\u062F \u0634\u0648\u062F."
        };
        if (page === 'otp' && s.isAuthenticated) return {
            redirect: 'checkout',
            reason: null
        };
        if ((0, $9dfdde2870d5f6f9$export$1630b5343357e8b5).BLOCKED_WHEN_CLOSED.includes(page) && !s.restaurantConfig.isOpen) return {
            redirect: 'menu',
            reason: "\u0631\u0633\u062A\u0648\u0631\u0627\u0646 \u0647\u0645\u200C\u0627\u06A9\u0646\u0648\u0646 \u0628\u0633\u062A\u0647 \u0627\u0633\u062A."
        };
        return null;
    }
    // ── Cart ──────────────────────────────────────────────────────────────────
    generateConfigId(productId, addons = []) {
        return `${productId}:${[
            ...addons
        ].sort().join(',')}`;
    }
    calculateLinePrice(product, selectedAddons = []) {
        return selectedAddons.reduce((total, name)=>{
            const idx = (product.afzoodanis || []).indexOf(name);
            return total + (idx !== -1 ? (product.gheymat_afzoodanis || [])[idx] || 0 : 0);
        }, product.price);
    }
    addToCart(productId, selectedAddons = []) {
        const { restaurantConfig: restaurantConfig, cart: cart } = this.#state;
        const product = this.#findProduct(productId);
        if (!restaurantConfig.isOpen) throw new Error("\u0631\u0633\u062A\u0648\u0631\u0627\u0646 \u0647\u0645\u200C\u0627\u06A9\u0646\u0648\u0646 \u0628\u0633\u062A\u0647 \u0627\u0633\u062A.");
        if (!product) throw new Error("\u0645\u062D\u0635\u0648\u0644 \u06CC\u0627\u0641\u062A \u0646\u0634\u062F.");
        if (!product.available) throw new Error("\u0627\u06CC\u0646 \u0622\u06CC\u062A\u0645 \u062F\u0631 \u062D\u0627\u0644 \u062D\u0627\u0636\u0631 \u0646\u0627\u0645\u0648\u062C\u0648\u062F \u0627\u0633\u062A.");
        const configId = this.generateConfigId(productId, selectedAddons);
        const linePrice = this.calculateLinePrice(product, selectedAddons);
        const copy = [
            ...cart
        ];
        const idx = copy.findIndex((i)=>i.id === configId);
        if (idx !== -1) copy[idx] = {
            ...copy[idx],
            quantity: copy[idx].quantity + 1
        };
        else copy.push({
            id: configId,
            productId: productId,
            selectedAddons: [
                ...selectedAddons
            ],
            quantity: 1,
            linePrice: linePrice
        });
        this.setState({
            cart: copy
        });
    }
    removeFromCart(productId, selectedAddons = []) {
        const configId = this.generateConfigId(productId, selectedAddons);
        const copy = [
            ...this.#state.cart
        ];
        const idx = copy.findIndex((i)=>i.id === configId);
        if (idx === -1) return;
        if (copy[idx].quantity > 1) copy[idx] = {
            ...copy[idx],
            quantity: copy[idx].quantity - 1
        };
        else copy.splice(idx, 1);
        this.setState({
            cart: copy
        });
    }
    removeLastConfigOfProduct(productId) {
        const items = this.#state.cart.filter((i)=>i.productId === productId);
        if (items.length === 0) return;
        this.removeFromCart(productId, items[items.length - 1].selectedAddons);
    }
    getProductTotalCount(productId) {
        return this.#state.cart.filter((i)=>i.productId === productId).reduce((s, i)=>s + i.quantity, 0);
    }
    clearCart() {
        this.setState({
            cart: [],
            appliedCoupon: null,
            couponCode: ''
        });
    }
    // ── Coupon ────────────────────────────────────────────────────────────────
    async applyCoupon(code) {
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) throw new Error("\u0644\u0637\u0641\u0627 \u06A9\u062F \u062A\u062E\u0641\u06CC\u0641 \u0631\u0627 \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F.");
        const subtotal = this.computeSubtotal();
        const coupon = await (0, $73936c209f170d43$export$ef5937a3f5701761)(trimmed);
        if (!coupon) throw new Error("\u06A9\u062F \u062A\u062E\u0641\u06CC\u0641 \u0648\u0627\u0631\u062F \u0634\u062F\u0647 \u0627\u0634\u062A\u0628\u0627\u0647 \u06CC\u0627 \u0645\u0646\u0642\u0636\u06CC \u0627\u0633\u062A.");
        if (subtotal < coupon.minOrder) throw new Error(`\u{62D}\u{62F}\u{627}\u{642}\u{644} \u{645}\u{628}\u{644}\u{63A} \u{62E}\u{631}\u{6CC}\u{62F} \u{628}\u{631}\u{627}\u{6CC} \u{627}\u{6CC}\u{646} \u{6A9}\u{62F} ${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(coupon.minOrder)} \u{627}\u{633}\u{62A}.`);
        this.setState({
            appliedCoupon: coupon,
            couponCode: trimmed
        });
        return {
            coupon: coupon,
            discount: this.#computeDiscount(subtotal, coupon)
        };
    }
    // ── Financials ────────────────────────────────────────────────────────────
    computeSubtotal() {
        return this.#state.cart.reduce((s, i)=>s + i.linePrice * i.quantity, 0);
    }
    #computeDiscount(subtotal, coupon) {
        if (!coupon) return 0;
        if (coupon.type === 'percent') return Math.floor(subtotal * coupon.value / 100);
        if (coupon.type === 'flat') return coupon.value;
        return 0;
    }
    computeDeliveryFee(orderType) {
        if (orderType !== 'delivery') return 0;
        const { restaurantConfig: restaurantConfig } = this.#state;
        return this.computeSubtotal() >= restaurantConfig.deliveryFeeThreshold ? 0 : restaurantConfig.defaultDeliveryFee;
    }
    computeFinancials(orderType) {
        const subtotal = this.computeSubtotal();
        const discount = this.#computeDiscount(subtotal, this.#state.appliedCoupon);
        const deliveryFee = this.computeDeliveryFee(orderType || this.#state.orderType);
        return {
            subtotal: subtotal,
            discount: discount,
            deliveryFee: deliveryFee,
            grandTotal: subtotal - discount + deliveryFee
        };
    }
    // ── Menu helpers ──────────────────────────────────────────────────────────
    #findProduct(id) {
        return (this.#state.menuData?.products || []).find((p)=>p.id === id);
    }
    getProduct(id) {
        return this.#findProduct(id);
    }
    getCategories() {
        return this.#state.menuData?.categories || [];
    }
    getProducts() {
        return this.#state.menuData?.products || [];
    }
    /** Update a single product's availability from a real-time push */ updateProductAvailability(productId, available) {
        const menuData = this.#state.menuData;
        if (!menuData) return;
        const products = menuData.products.map((p)=>p.id === productId ? {
                ...p,
                available: available
            } : p);
        this.setState({
            menuData: {
                ...menuData,
                products: products
            }
        });
    }
    /** Update restaurant open/close status from a real-time push */ updateRestaurantStatus(isOpen) {
        this.setState({
            restaurantConfig: {
                ...this.#state.restaurantConfig,
                isOpen: isOpen
            }
        });
    }
}


/**
 * PRESENTER — Orchestrates Model ↔ View.
 * All user-facing logic lives here. addEventListener calls live in view.base.bindStaticEvents.
 */ 
/**
 * VIEW BASE — Shared DOM utilities.
 * addEventListener calls for all static HTML elements live here.
 * Presenter injects callbacks — view.base has zero business logic.
 */ 
function $0cdd358936d3140a$export$f6cef6fcf9ca4e17(message, type = 'info') {
    const container = document.getElementById('toast-container');
    if (!container) return;
    const icons = {
        success: 'fa-circle-check',
        error: 'fa-circle-exclamation',
        info: 'fa-circle-info'
    };
    const toast = document.createElement('div');
    toast.className = `toast-msg ${type}`;
    toast.innerHTML = `<i class="fa-solid ${icons[type] || icons.info}"></i> <span>${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(message)}</span>`;
    container.appendChild(toast);
    setTimeout(()=>{
        toast.style.opacity = '0';
        setTimeout(()=>toast.remove(), 300);
    }, 3500);
}
function $0cdd358936d3140a$export$f808c2be2eb32cc5(pageId) {
    document.querySelectorAll('.page-view').forEach((p)=>p.classList.add('hidden'));
    document.getElementById(`page-${pageId}`)?.classList.remove('hidden');
}
function $0cdd358936d3140a$export$79caa320eed3dcc3(btnId, loading, defaultHtml) {
    const btn = document.getElementById(btnId);
    if (!btn) return;
    btn.disabled = loading;
    btn.innerHTML = loading ? `<i class="fa-solid fa-spinner fa-spin"></i> \u{644}\u{637}\u{641}\u{627} \u{635}\u{628}\u{631} \u{6A9}\u{646}\u{6CC}\u{62F}...` : defaultHtml;
}
function $0cdd358936d3140a$export$dead20abe39d024c(address) {
    const el = document.getElementById('map-selected-address');
    if (el) el.textContent = address || '';
}
function $0cdd358936d3140a$export$8a2a893fc4600b33(callbacks) {
    // ── data-navigate delegation ─────────────────────────────────────────────
    // Single delegated listener handles ALL back buttons and "return to menu"
    // buttons via data-navigate="pageName" attribute — no onclick="" needed.
    document.addEventListener('click', (e)=>{
        const target = e.target.closest('[data-navigate]');
        if (!target) return;
        const page = target.getAttribute('data-navigate');
        if (page) callbacks.onNavigate(page);
    });
    // ── Addon sheet ──────────────────────────────────────────────────────────
    document.getElementById('btn-submit-addons')?.addEventListener('click', callbacks.onAddonSubmit);
    document.getElementById('btn-close-sheet')?.addEventListener('click', callbacks.onAddonClose);
    document.getElementById('bottom-sheet-backdrop')?.addEventListener('click', callbacks.onAddonClose);
    // ── Invoice ──────────────────────────────────────────────────────────────
    document.getElementById('btn-clear-cart')?.addEventListener('click', callbacks.onClearCart);
    document.getElementById('btn-apply-coupon')?.addEventListener('click', callbacks.onApplyCoupon);
    document.getElementById('btn-invoice-submit')?.addEventListener('click', callbacks.onInvoiceSubmit);
    document.getElementById('order-notes')?.addEventListener('input', (e)=>callbacks.onNotesChange(e.target.value));
    // ── Floating cart ────────────────────────────────────────────────────────
    document.getElementById('floating-cart')?.addEventListener('click', callbacks.onFloatingCartClick);
    // ── OTP ──────────────────────────────────────────────────────────────────
    document.getElementById('btn-send-otp')?.addEventListener('click', callbacks.onSendOtp);
    document.getElementById('btn-verify-otp')?.addEventListener('click', callbacks.onVerifyOtp);
    document.getElementById('btn-resend-otp')?.addEventListener('click', callbacks.onResendOtp);
    // btn-change-phone uses data-navigate="otp" — handled by delegation above
    // but also needs special logic (clear OTP state), so we add it here too
    document.getElementById('btn-change-phone')?.addEventListener('click', callbacks.onChangePhone);
    // OTP digit auto-focus chain
    document.querySelectorAll('.otp-digit-box').forEach((box, idx, boxes)=>{
        box.addEventListener('input', (e)=>{
            e.target.value = e.target.value.replace(/[^0-9]/g, '');
            if (e.target.value.length === 1 && idx < boxes.length - 1) boxes[idx + 1].focus();
        });
        box.addEventListener('keydown', (e)=>{
            if (e.key === 'Backspace' && !box.value && idx > 0) boxes[idx - 1].focus();
        });
    });
    // ── Checkout ─────────────────────────────────────────────────────────────
    document.getElementById('btn-checkout-submit')?.addEventListener('click', callbacks.onCheckoutSubmit);
    // ── Payment failed ───────────────────────────────────────────────────────
    document.getElementById('btn-retry-payment')?.addEventListener('click', callbacks.onRetryPayment);
    document.getElementById('btn-failed-back-menu')?.addEventListener('click', callbacks.onBackToMenu);
    // ── Logout (optional — add id="btn-logout" anywhere in HTML) ─────────────
    document.getElementById('btn-logout')?.addEventListener('click', callbacks.onLogout);
}


/**
 * VIEW MENU — منوی اصلی رستوران
 */ 
function $7a002140c5914afe$export$429ee859e6a27934() {
    document.getElementById('menu-sections-container').innerHTML = `
    <div class="loading-state" style="text-align:center;padding:60px 20px;">
      <i class="fa-solid fa-spinner fa-spin" style="font-size:2rem;color:var(--primary);margin-bottom:12px;"></i>
      <p style="color:var(--text-muted);font-size:0.9rem;">\u{62F}\u{631} \u{62D}\u{627}\u{644} \u{628}\u{627}\u{631}\u{6AF}\u{630}\u{627}\u{631}\u{6CC} \u{645}\u{646}\u{648}...</p>
    </div>`;
    document.getElementById('category-tabs-container').innerHTML = '';
}
function $7a002140c5914afe$export$f18becb2f09f38ed(message, onRetry) {
    document.getElementById('menu-sections-container').innerHTML = `
    <div class="loading-state" style="text-align:center;padding:60px 20px;">
      <i class="fa-solid fa-triangle-exclamation" style="font-size:2rem;color:var(--primary);margin-bottom:12px;"></i>
      <p style="color:var(--text-muted);font-size:0.9rem;margin-bottom:16px;">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(message)}</p>
      <button id="btn-menu-retry" class="action-button" style="margin:0 auto;">
        <i class="fa-solid fa-rotate-right"></i> <span>\u{62A}\u{644}\u{627}\u{634} \u{645}\u{62C}\u{62F}\u{62F}</span>
      </button>
    </div>`;
    document.getElementById('btn-menu-retry')?.addEventListener('click', onRetry);
}
function $7a002140c5914afe$export$70f5b6c4f2bee4f1(config) {
    const badge = document.getElementById('restaurant-status-badge');
    if (!badge) return;
    badge.innerHTML = config.isOpen ? `<span class="status-badge"><i class="fa-solid fa-circle"></i> \u{631}\u{633}\u{62A}\u{648}\u{631}\u{627}\u{646} \u{628}\u{627}\u{632} \u{627}\u{633}\u{62A}</span>` : `<span class="status-badge closed"><i class="fa-solid fa-circle-xmark"></i> \u{628}\u{633}\u{62A}\u{647} (\u{633}\u{627}\u{639}\u{62A} \u{6A9}\u{627}\u{631}: ${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(config.openTime)})</span>`;
}
async function $7a002140c5914afe$export$8807aa86dc1a7379(categories, products, callbacks) {
    $7a002140c5914afe$var$_buildTabs(categories, callbacks.onTabClick);
    $7a002140c5914afe$var$_buildSections(categories, products, callbacks);
    // Preload all product images into cache in the background
    const urls = products.map((p)=>p.image).filter(Boolean);
    (0, $08fa37fff71de28c$export$51adf995cb4f5799).preload(urls);
    // Fix sticky category bar: ensure parent has correct positioning
    const tabsWrapper = document.querySelector('.category-tabs-container');
    if (tabsWrapper) {
        tabsWrapper.style.position = 'sticky';
        tabsWrapper.style.top = '0';
        tabsWrapper.style.zIndex = '100';
    }
}
function $7a002140c5914afe$var$_buildTabs(categories, onTabClick) {
    const container = document.getElementById('category-tabs-container');
    if (!container) return;
    container.innerHTML = '';
    categories.forEach((cat, idx)=>{
        const tab = document.createElement('div');
        tab.className = `category-tab ${idx === 0 ? 'active' : ''}`;
        tab.id = `tab-${cat.id}`;
        tab.textContent = cat.name;
        tab.addEventListener('click', ()=>onTabClick(cat.id));
        container.appendChild(tab);
    });
}
function $7a002140c5914afe$var$_buildSections(categories, products, callbacks) {
    const container = document.getElementById('menu-sections-container');
    if (!container) return;
    container.innerHTML = '';
    categories.forEach((cat)=>{
        const catProducts = products.filter((p)=>p.category === cat.id);
        if (catProducts.length === 0) return;
        const section = document.createElement('section');
        section.id = `section-${cat.id}`;
        section.className = 'menu-category-section';
        const title = document.createElement('h2');
        title.className = 'section-title';
        title.textContent = cat.name;
        section.appendChild(title);
        catProducts.forEach((prod)=>section.appendChild($7a002140c5914afe$var$_createCard(prod, callbacks)));
        container.appendChild(section);
    });
}
function $7a002140c5914afe$var$_createCard(prod) {
    const wrapper = document.createElement('div');
    wrapper.className = 'product-card-container';
    wrapper.id = `card-container-${prod.id}`;
    let badge = prod.badge ? `<span class="badge product-image-badge">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(prod.badge)}</span>` : '';
    if (!prod.available) badge = `<span class="badge badge-out product-image-badge">\u{646}\u{627}\u{645}\u{648}\u{62C}\u{648}\u{62F}</span>`;
    // Use cached image if available, fall back to network
    const cached = (0, $08fa37fff71de28c$export$51adf995cb4f5799).get(prod.image);
    const imgSrc = cached || prod.image || `https://placehold.co/150x150/ff5c35/ffffff?text=${encodeURIComponent(prod.name)}`;
    const card = document.createElement('div');
    card.className = `product-card ${!prod.available ? 'unavailable' : ''}`;
    card.innerHTML = `
    <div class="product-image-side">
      <img src="${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(imgSrc)}" alt="${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(prod.name)}">
      ${badge}
    </div>
    <div class="product-content-side">
      <div>
        <h3 class="product-name">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(prod.name)}</h3>
        <p class="product-desc">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(prod.description)}</p>
      </div>
      <div class="product-footer">
        <div class="price-tag">${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(prod.price)}</div>
        <div class="action-zone" id="action-zone-${prod.id}"></div>
      </div>
    </div>`;
    wrapper.appendChild(card);
    const expanded = document.createElement('div');
    expanded.className = 'expanded-configs hidden';
    expanded.id = `expanded-configs-${prod.id}`;
    wrapper.appendChild(expanded);
    return wrapper;
}
function $7a002140c5914afe$export$c036cf6106c1fc5b(prod, totalQty, cartItems, isOpen, callbacks) {
    const actionZone = document.getElementById(`action-zone-${prod.id}`);
    const configsZone = document.getElementById(`expanded-configs-${prod.id}`);
    if (!actionZone || !configsZone) return;
    // اگر محصول هیچ افزودنی‌ای ندارد
    if (!prod.afzoodanis || prod.afzoodanis.length === 0) {
        // مخفی‌سازی expanded-configs و خالی کردن آن
        configsZone.classList.add('hidden');
        configsZone.innerHTML = '';
        // نمایش دکمهٔ عادی در action-zone (مثل حالت مودال ولی بدون باز شدن مودال)
        actionZone.innerHTML = '';
        if (!isOpen) actionZone.innerHTML = `<button class="action-button" disabled style="font-size:.75rem;">\u{628}\u{633}\u{62A}\u{647} \u{627}\u{633}\u{62A}</button>`;
        else if (!prod.available) actionZone.innerHTML = `<button class="action-button" disabled style="font-size:.75rem;">\u{646}\u{627}\u{645}\u{648}\u{62C}\u{648}\u{62F}</button>`;
        else if (totalQty === 0) {
            const btn = document.createElement('button');
            btn.className = 'action-button';
            btn.innerHTML = `<i class="fa-solid fa-plus"></i> <span>\u{627}\u{641}\u{632}\u{648}\u{62F}\u{646}</span>`;
            btn.addEventListener('click', (e)=>{
                e.stopPropagation();
                callbacks.onAddClick(prod);
            });
            actionZone.appendChild(btn);
        } else {
            const ctrl = document.createElement('div');
            ctrl.className = 'quantity-control';
            ctrl.innerHTML = `
      <button class="btn-qty btn-minus"><i class="fa-solid fa-minus"></i></button>
      <span class="qty-val">${totalQty}</span>
      <button class="btn-qty btn-plus"><i class="fa-solid fa-plus"></i></button>`;
            ctrl.querySelector('.btn-plus').addEventListener('click', (e)=>{
                e.stopPropagation();
                callbacks.onAddClick(prod);
            });
            ctrl.querySelector('.btn-minus').addEventListener('click', (e)=>{
                e.stopPropagation();
                callbacks.onRemoveClick(prod.id);
            });
            actionZone.appendChild(ctrl);
        }
        return; // کار تمام است و دیگر ادامه نمی‌دهیم
    }
    // فقط محصولاتی که دقیقاً true باشند مودال می‌شوند
    const useModal = prod.showAfzoodanisInModal === true;
    if (!useModal) {
        // ── حالت مستقیم: دکمۀ اصلی حذف، افزودنی‌ها زیر کارت ──
        actionZone.innerHTML = ''; // دکمۀ "افزودن" کارت حذف شود
        configsZone.classList.remove('hidden'); // همیشه نمایش
        configsZone.innerHTML = '';
        if (!isOpen) {
            configsZone.innerHTML = `<div style="text-align:center;padding:8px;"><button class="action-button" disabled>\u{628}\u{633}\u{62A}\u{647} \u{627}\u{633}\u{62A}</button></div>`;
            return;
        }
        if (!prod.available) {
            configsZone.innerHTML = `<div style="text-align:center;padding:8px;"><button class="action-button" disabled>\u{646}\u{627}\u{645}\u{648}\u{62C}\u{648}\u{62F}</button></div>`;
            return;
        }
        // گزینه‌ها: ساده + هر افزودنی (هر کدام یک ردیف مجزا)
        const options = [];
        options.push({
            label: "\u0633\u0627\u062F\u0647",
            selectedAddons: [],
            priceDelta: 0
        });
        (prod.afzoodanis || []).forEach((addon, idx)=>{
            const delta = (prod.gheymat_afzoodanis || [])[idx] || 0;
            options.push({
                label: addon,
                selectedAddons: [
                    addon
                ],
                priceDelta: delta
            });
        });
        options.forEach((opt)=>{
            const linePrice = prod.price + opt.priceDelta;
            const existing = cartItems.find((item)=>item.selectedAddons.length === opt.selectedAddons.length && item.selectedAddons.every((a, i)=>a === opt.selectedAddons[i]));
            const row = document.createElement('div');
            row.className = 'config-row';
            if (existing) {
                row.innerHTML = `
          <div class="config-details">
            <span class="config-addons">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(opt.label)}</span>
            <span class="text-muted" style="font-size:.68rem;">\u{642}\u{6CC}\u{645}\u{62A} \u{648}\u{627}\u{62D}\u{62F}: ${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(linePrice)}</span>
          </div>
          <div class="config-right">
            <span class="config-price">${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(linePrice * existing.quantity)}</span>
            <div class="quantity-control" style="transform:scale(.95);">
              <button class="btn-qty btn-sub-minus"><i class="fa-solid fa-minus"></i></button>
              <span class="qty-val">${existing.quantity}</span>
              <button class="btn-qty btn-sub-plus"><i class="fa-solid fa-plus"></i></button>
            </div>
          </div>`;
                row.querySelector('.btn-sub-plus').addEventListener('click', (e)=>{
                    e.stopPropagation();
                    callbacks.onConfigAdd(prod.id, opt.selectedAddons);
                });
                row.querySelector('.btn-sub-minus').addEventListener('click', (e)=>{
                    e.stopPropagation();
                    callbacks.onConfigRemove(prod.id, opt.selectedAddons);
                });
            } else {
                row.innerHTML = `
          <div class="config-details">
            <span class="config-addons">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(opt.label)}</span>
            <span class="text-muted" style="font-size:.68rem;">${opt.priceDelta >= 0 ? '+' + (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(opt.priceDelta) : (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(opt.priceDelta)}</span>
          </div>
          <div class="config-right">
            <button class="action-button" style="font-size:.75rem; padding:4px 12px;">
              <i class="fa-solid fa-plus"></i> <span>\u{627}\u{641}\u{632}\u{648}\u{62F}\u{646}</span>
            </button>
          </div>`;
                row.querySelector('.action-button').addEventListener('click', (e)=>{
                    e.stopPropagation();
                    callbacks.onConfigAdd(prod.id, opt.selectedAddons);
                });
            }
            configsZone.appendChild(row);
        });
        return;
    }
    // ── حالت مودال (showAfzoodanisInModal === true) ──
    actionZone.innerHTML = '';
    if (!isOpen) actionZone.innerHTML = `<button class="action-button" disabled style="font-size:.75rem;">\u{628}\u{633}\u{62A}\u{647} \u{627}\u{633}\u{62A}</button>`;
    else if (!prod.available) actionZone.innerHTML = `<button class="action-button" disabled style="font-size:.75rem;">\u{646}\u{627}\u{645}\u{648}\u{62C}\u{648}\u{62F}</button>`;
    else if (totalQty === 0) {
        const btn = document.createElement('button');
        btn.className = 'action-button';
        btn.innerHTML = `<i class="fa-solid fa-plus"></i> <span>\u{627}\u{641}\u{632}\u{648}\u{62F}\u{646}</span>`;
        btn.addEventListener('click', (e)=>{
            e.stopPropagation();
            callbacks.onAddClick(prod);
        });
        actionZone.appendChild(btn);
    } else {
        const ctrl = document.createElement('div');
        ctrl.className = 'quantity-control';
        ctrl.innerHTML = `
      <button class="btn-qty btn-minus"><i class="fa-solid fa-minus"></i></button>
      <span class="qty-val">${totalQty}</span>
      <button class="btn-qty btn-plus"><i class="fa-solid fa-plus"></i></button>`;
        ctrl.querySelector('.btn-plus').addEventListener('click', (e)=>{
            e.stopPropagation();
            callbacks.onAddClick(prod);
        });
        ctrl.querySelector('.btn-minus').addEventListener('click', (e)=>{
            e.stopPropagation();
            callbacks.onRemoveClick(prod.id);
        });
        actionZone.appendChild(ctrl);
    }
    $7a002140c5914afe$var$_updateConfigRows(configsZone, prod, totalQty, cartItems, callbacks);
}
function $7a002140c5914afe$var$_updateConfigRows(container, prod, totalQty, cartItems, callbacks) {
    if (!container) return;
    if (totalQty === 0 || cartItems.length === 0) {
        container.innerHTML = '';
        container.classList.add('hidden');
        return;
    }
    container.innerHTML = '';
    container.classList.remove('hidden');
    cartItems.forEach((config)=>{
        const summary = config.selectedAddons.length > 0 ? config.selectedAddons.join(' + ') : "\u0633\u0627\u062F\u0647 (\u0628\u062F\u0648\u0646 \u0627\u0641\u0632\u0648\u062F\u0646\u06CC)";
        const row = document.createElement('div');
        row.className = 'config-row';
        row.innerHTML = `
      <div class="config-details">
        <span class="config-addons">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(summary)}</span>
        <span class="text-muted" style="font-size:.68rem;">\u{642}\u{6CC}\u{645}\u{62A} \u{648}\u{627}\u{62D}\u{62F}: ${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(config.linePrice)}</span>
      </div>
      <div class="config-right">
        <span class="config-price">${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(config.linePrice * config.quantity)}</span>
        <div class="quantity-control" style="transform:scale(.95);">
          <button class="btn-qty btn-sub-minus"><i class="fa-solid fa-minus"></i></button>
          <span class="qty-val">${config.quantity}</span>
          <button class="btn-qty btn-sub-plus"><i class="fa-solid fa-plus"></i></button>
        </div>
      </div>`;
        row.querySelector('.btn-sub-plus').addEventListener('click', (e)=>{
            e.stopPropagation();
            callbacks.onConfigAdd(prod.id, config.selectedAddons);
        });
        row.querySelector('.btn-sub-minus').addEventListener('click', (e)=>{
            e.stopPropagation();
            callbacks.onConfigRemove(prod.id, config.selectedAddons);
        });
        container.appendChild(row);
    });
}
function $7a002140c5914afe$export$6ab60f5372d5a993(categoryId) {
    document.querySelectorAll('.category-tab').forEach((t)=>t.classList.remove('active'));
    const tab = document.getElementById(`tab-${categoryId}`);
    if (tab) tab.classList.add('active');
}
function $7a002140c5914afe$export$ba0a7bf33eb6f76(categoryId) {
    document.getElementById(`section-${categoryId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
    });
}
function $7a002140c5914afe$export$b75578d5e5634372(totalCount, totalAmount) {
    const bar = document.getElementById('floating-cart');
    if (!bar) return;
    if (totalCount > 0) {
        bar.classList.remove('hidden');
        document.getElementById('floating-cart-qty').textContent = totalCount.toLocaleString('fa-IR');
        document.getElementById('floating-cart-total').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(totalAmount);
    } else bar.classList.add('hidden');
}
function $7a002140c5914afe$export$80acc5193cd9ab1(product, onPriceChange) {
    document.getElementById('sheet-addon-title').textContent = "\u0627\u0646\u062A\u062E\u0627\u0628 \u0627\u0641\u0632\u0648\u062F\u0646\u06CC\u200C\u0647\u0627";
    document.getElementById('sheet-addon-subtitle').textContent = product.name;
    document.getElementById('sheet-addons-total-price').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(product.price);
    const list = document.getElementById('addons-list-container');
    list.innerHTML = '';
    (product.afzoodanis || []).forEach((addon, idx)=>{
        const delta = (product.gheymat_afzoodanis || [])[idx] || 0;
        const deltaText = delta >= 0 ? `+${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(delta)}` : (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(delta);
        const row = document.createElement('div');
        row.className = 'addon-row';
        row.innerHTML = `
      <div class="addon-label-side">
        <input type="checkbox" class="addon-checkbox" id="chk-addon-${idx}" value="${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(addon)}">
        <label for="chk-addon-${idx}">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(addon)}</label>
      </div>
      <div class="addon-price ${delta >= 0 ? 'text-secondary' : 'text-primary'}">${deltaText}</div>`;
        const refresh = ()=>onPriceChange($7a002140c5914afe$export$d904cc079f49912f());
        row.addEventListener('click', (e)=>{
            if (e.target.tagName !== 'INPUT') {
                row.querySelector('.addon-checkbox').checked = !row.querySelector('.addon-checkbox').checked;
                refresh();
            }
        });
        row.querySelector('.addon-checkbox').addEventListener('change', refresh);
        list.appendChild(row);
    });
    document.getElementById('bottom-sheet-backdrop').classList.add('active');
    document.getElementById('addon-bottom-sheet').classList.add('active');
}
function $7a002140c5914afe$export$bc7916b895868493() {
    document.getElementById('bottom-sheet-backdrop').classList.remove('active');
    document.getElementById('addon-bottom-sheet').classList.remove('active');
}
function $7a002140c5914afe$export$d904cc079f49912f() {
    return Array.from(document.querySelectorAll('.addon-checkbox:checked')).map((c)=>c.value);
}
function $7a002140c5914afe$export$c0e4d383376a4a24(amount) {
    document.getElementById('sheet-addons-total-price').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(amount);
}


/**
 * VIEW INVOICE — سبد خرید و فاکتور
 */ 
function $709694b8ee5edd76$export$b3890eb0ae9dca99(cart, products, financials, orderNotes, callbacks) {
    const emptyState = document.getElementById('cart-empty-state');
    const activeContent = document.getElementById('cart-active-content');
    const stickyBar = document.getElementById('invoice-sticky-bar');
    if (cart.length === 0) {
        emptyState.classList.remove('hidden');
        activeContent.classList.add('hidden');
        stickyBar.classList.add('hidden');
        return;
    }
    emptyState.classList.add('hidden');
    activeContent.classList.remove('hidden');
    stickyBar.classList.remove('hidden');
    $709694b8ee5edd76$var$_renderItems(cart, products, callbacks);
    $709694b8ee5edd76$var$_renderTotals(financials);
    const notesTA = document.getElementById('order-notes');
    if (notesTA) notesTA.value = orderNotes || '';
}
function $709694b8ee5edd76$var$_renderItems(cart, products, callbacks) {
    const list = document.getElementById('invoice-items-list');
    list.innerHTML = '';
    cart.forEach((item)=>{
        const prod = products.find((p)=>p.id === item.productId);
        if (!prod) return;
        const addonsText = item.selectedAddons.length > 0 ? item.selectedAddons.join(' + ') : "\u0633\u0627\u062F\u0647";
        const cached = (0, $08fa37fff71de28c$export$51adf995cb4f5799).get(prod.image);
        const imgSrc = cached || prod.image || `https://placehold.co/150x150/ff5c35/ffffff?text=${encodeURIComponent(prod.name)}`;
        const row = document.createElement('div');
        row.className = 'cart-item-row';
        row.innerHTML = `
      <div class="cart-item-info">
        <div class="cart-item-thumbnail">
          <img src="${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(imgSrc)}" alt="${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(prod.name)}"
               onerror="this.src='https://placehold.co/150x150/ff5c35/ffffff?text=%D8%BA%D8%B0%D8%A7'">
        </div>
        <div class="cart-item-details">
          <span class="cart-item-title">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(prod.name)}</span>
          <span class="cart-item-addons"><i class="fa-solid fa-square-plus"></i> ${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(addonsText)}</span>
        </div>
      </div>
      <div class="cart-item-actions">
        <span class="price-tag" style="font-size:.82rem;">${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(item.linePrice * item.quantity)}</span>
        <div class="quantity-control" style="transform:scale(.9);">
          <button class="btn-qty btn-item-minus"><i class="fa-solid fa-minus"></i></button>
          <span class="qty-val">${item.quantity}</span>
          <button class="btn-qty btn-item-plus"><i class="fa-solid fa-plus"></i></button>
        </div>
      </div>`;
        row.querySelector('.btn-item-plus').addEventListener('click', ()=>callbacks.onItemAdd(item.productId, item.selectedAddons));
        row.querySelector('.btn-item-minus').addEventListener('click', ()=>callbacks.onItemRemove(item.productId, item.selectedAddons));
        list.appendChild(row);
    });
}
function $709694b8ee5edd76$var$_renderTotals(financials) {
    document.getElementById('summary-subtotal').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.subtotal);
    const discountRow = document.getElementById('summary-discount-row');
    if (financials.discount > 0) {
        discountRow.classList.remove('hidden');
        document.getElementById('summary-discount').textContent = `-${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.discount)}`;
    } else discountRow.classList.add('hidden');
    const deliveryEl = document.getElementById('summary-delivery');
    deliveryEl.textContent = financials.deliveryFee === 0 ? "\u0631\u0627\u06CC\u06AF\u0627\u0646" : (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.deliveryFee);
    deliveryEl.classList.toggle('text-secondary', financials.deliveryFee === 0);
    document.getElementById('summary-grandtotal').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.grandTotal);
    document.getElementById('invoice-payable-total').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.grandTotal);
}
function $709694b8ee5edd76$export$d0e7b6cbe1ae6c1e(text, isSuccess) {
    const el = document.getElementById('coupon-message');
    if (!el) return;
    el.className = isSuccess ? 'text-secondary' : 'text-primary';
    el.textContent = text;
}


/**
 * VIEW OTP — احراز هویت
 */ 

function $e3b051dd7ebf4358$export$afa434b436311169() {
    document.getElementById('otp-step-phone').classList.remove('hidden');
    document.getElementById('otp-step-code').classList.add('hidden');
}
function $e3b051dd7ebf4358$export$6bc9a3e15068d5a4(phone) {
    document.getElementById('otp-step-phone').classList.add('hidden');
    document.getElementById('otp-step-code').classList.remove('hidden');
    document.getElementById('otp-code-subtitle').innerHTML = `\u{6A9}\u{62F} \u{6F4} \u{631}\u{642}\u{645}\u{6CC} \u{627}\u{631}\u{633}\u{627}\u{644} \u{634}\u{62F}\u{647} \u{628}\u{647} <span style="font-weight:bold;color:var(--dark);direction:ltr;display:inline-block;">${(0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(phone)}</span> \u{631}\u{627} \u{648}\u{627}\u{631}\u{62F} \u{646}\u{645}\u{627}\u{6CC}\u{6CC}\u{62F}.`;
}
function $e3b051dd7ebf4358$export$1ad018ea9ed1d4c7(sentAt, onExpired) {
    const countdownEl = document.getElementById('otp-countdown');
    const timerBox = document.getElementById('otp-timer-box');
    const btnResend = document.getElementById('btn-resend-otp');
    timerBox.classList.remove('hidden');
    btnResend.classList.add('hidden');
    const tick = ()=>{
        const remaining = (0, $9dfdde2870d5f6f9$export$1630b5343357e8b5).OTP_COUNTDOWN_SEC - Math.floor((Date.now() - sentAt) / 1000);
        if (remaining <= 0) {
            timerBox.classList.add('hidden');
            btnResend.classList.remove('hidden');
            onExpired?.();
        } else countdownEl.textContent = remaining.toLocaleString('fa-IR');
    };
    tick();
    return setInterval(tick, 1000);
}
function $e3b051dd7ebf4358$export$7351e904ef366a5() {
    document.querySelectorAll('.otp-digit-box').forEach((b)=>b.value = '');
}
function $e3b051dd7ebf4358$export$9a0a9c75ccef13fb() {
    let code = '';
    document.querySelectorAll('.otp-digit-box').forEach((b)=>code += b.value);
    return code;
}


/**
 * VIEW CHECKOUT — جزئیات سفارش، آدرس، و نقشه
 */ 

let $09a416bfe8ccd350$var$_orderTypeListenerAttached = false;
let $09a416bfe8ccd350$var$_onOrderTypeChangeCb = null;
let $09a416bfe8ccd350$var$_mapInstance = null;
let $09a416bfe8ccd350$var$_mapMarker = null;
function $09a416bfe8ccd350$export$b3890eb0ae9dca99(state, financials, callbacks) {
    $09a416bfe8ccd350$var$_prefillReceiverInfo(state);
    $09a416bfe8ccd350$var$_setupOrderTypeTabs(state.orderType, callbacks.onOrderTypeChange);
    $09a416bfe8ccd350$var$_toggleDeliveryFields(state.orderType);
    $09a416bfe8ccd350$var$_renderTotals(state.orderType, financials);
}
function $09a416bfe8ccd350$var$_prefillReceiverInfo(state) {
    const phoneInput = document.getElementById('receiver-phone');
    const nameInput = document.getElementById('receiver-name');
    const addressInput = document.getElementById('receiver-address');
    if (phoneInput && !phoneInput.value) phoneInput.value = state.receiverPhone || '';
    if (nameInput && !nameInput.value) nameInput.value = state.receiverName || '';
    if (addressInput && !addressInput.value) addressInput.value = state.receiverAddress || '';
}
function $09a416bfe8ccd350$var$_setupOrderTypeTabs(activeType, onChange) {
    const container = document.querySelector('.order-type-tabs');
    if (!container) return;
    if (!$09a416bfe8ccd350$var$_orderTypeListenerAttached) {
        container.addEventListener('click', (e)=>{
            const card = e.target.closest('.order-type-card');
            if (card && $09a416bfe8ccd350$var$_onOrderTypeChangeCb) $09a416bfe8ccd350$var$_onOrderTypeChangeCb(card.getAttribute('data-type'));
        });
        $09a416bfe8ccd350$var$_orderTypeListenerAttached = true;
    }
    $09a416bfe8ccd350$var$_onOrderTypeChangeCb = onChange;
    document.querySelectorAll('.order-type-card').forEach((card)=>{
        card.classList.toggle('active', card.getAttribute('data-type') === activeType);
    });
}
function $09a416bfe8ccd350$var$_toggleDeliveryFields(orderType) {
    const addressSection = document.getElementById('checkout-address-section');
    const mapSection = document.getElementById('checkout-map-section');
    const show = orderType === 'delivery';
    addressSection?.classList.toggle('hidden', !show);
    mapSection?.classList.toggle('hidden', !show);
}
function $09a416bfe8ccd350$var$_renderTotals(orderType, financials) {
    document.getElementById('checkout-summary-subtotal').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.subtotal);
    const discRow = document.getElementById('checkout-summary-discount-row');
    if (financials.discount > 0) {
        discRow?.classList.remove('hidden');
        document.getElementById('checkout-summary-discount').textContent = `-${(0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.discount)}`;
    } else discRow?.classList.add('hidden');
    const delivRow = document.getElementById('checkout-summary-delivery-row');
    if (orderType === 'delivery') {
        delivRow?.classList.remove('hidden');
        document.getElementById('checkout-summary-delivery').textContent = financials.deliveryFee === 0 ? "\u0631\u0627\u06CC\u06AF\u0627\u0646" : (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.deliveryFee);
    } else delivRow?.classList.add('hidden');
    document.getElementById('checkout-summary-grandtotal').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.grandTotal);
    document.getElementById('checkout-payable-total').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(financials.grandTotal);
}
function $09a416bfe8ccd350$export$e992d02417fcc76c() {
    return {
        name: document.getElementById('receiver-name')?.value.trim() || '',
        phone: document.getElementById('receiver-phone')?.value.trim() || '',
        address: document.getElementById('receiver-address')?.value.trim() || ''
    };
}
function $09a416bfe8ccd350$export$d8f76009e91cfb87(initialLocation, onLocationSelect) {
    const container = document.getElementById('map-picker-container');
    if (!container) return;
    const { provider: provider, apiKey: apiKey, defaultLat: defaultLat, defaultLng: defaultLng, defaultZoom: defaultZoom } = (0, $9dfdde2870d5f6f9$export$1630b5343357e8b5).MAP;
    const lat = initialLocation?.lat || defaultLat;
    const lng = initialLocation?.lng || defaultLng;
    if (provider === 'neshan') $09a416bfe8ccd350$var$_initNeshanMap(container, apiKey, lat, lng, defaultZoom, onLocationSelect);
    else if (provider === 'balad') $09a416bfe8ccd350$var$_initBaladMap(container, apiKey, lat, lng, defaultZoom, onLocationSelect);
    else $09a416bfe8ccd350$var$_initFallbackMap(container, lat, lng, onLocationSelect);
}
function $09a416bfe8ccd350$var$_initNeshanMap(container, apiKey, lat, lng, zoom, onSelect) {
    // Neshan map SDK (neshanmap.com)
    // Add <script src="https://static.neshan.org/sdk/openlayers/v1.1.7/ol.js"></script> to HTML
    if (!window.nmp_mapboxgl && !window.ol) {
        container.innerHTML = `<div class="map-placeholder"><p>SDK \u{646}\u{634}\u{627}\u{646} \u{628}\u{627}\u{631}\u{6AF}\u{630}\u{627}\u{631}\u{6CC} \u{646}\u{634}\u{62F}\u{647} \u{627}\u{633}\u{62A}</p></div>`;
        return;
    }
    // ── Real Neshan implementation ──
    // const map = new nmp_mapboxgl.Map({
    //   container: container.id || container,
    //   mapType: nmp_mapboxgl.Map.mapTypes.neshanVector,
    //   center: [lng, lat],
    //   zoom,
    //   mapKey: apiKey,
    //   poi: true,
    //   traffic: false,
    // });
    // const marker = new nmp_mapboxgl.Marker({ draggable: true })
    //   .setLngLat([lng, lat])
    //   .addTo(map);
    // marker.on('dragend', () => {
    //   const pos = marker.getLngLat();
    //   _reverseGeocode(pos.lat, pos.lng, apiKey).then(address => onSelect({ lat: pos.lat, lng: pos.lng, address }));
    // });
    // map.on('click', e => {
    //   marker.setLngLat([e.lngLat.lng, e.lngLat.lat]);
    //   _reverseGeocode(e.lngLat.lat, e.lngLat.lng, apiKey).then(address => onSelect({ lat: e.lngLat.lat, lng: e.lngLat.lng, address }));
    // });
    $09a416bfe8ccd350$var$_initFallbackMap(container, lat, lng, onSelect); // placeholder until SDK is loaded
}
function $09a416bfe8ccd350$var$_initBaladMap(container, apiKey, lat, lng, zoom, onSelect) {
    // Balad map SDK (developers.balad.ir)
    // Add <script src="https://balad.ir/api/mapkit/v4/mapkit.js" data-balad-token="API_KEY"></script>
    $09a416bfe8ccd350$var$_initFallbackMap(container, lat, lng, onSelect); // placeholder until SDK is loaded
}
function $09a416bfe8ccd350$var$_initFallbackMap(container, lat, lng, onSelect) {
    // Simple visual placeholder with manual coordinate input
    container.innerHTML = `
    <div class="map-placeholder" style="min-height:200px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; background:var(--bg-secondary); border-radius:var(--radius-md);">
      <i class="fa-solid fa-map-location-dot" style="font-size:2.5rem; color:var(--primary);"></i>
      <p style="font-size:0.82rem; color:var(--text-muted); text-align:center;">
        SDK \u{646}\u{642}\u{634}\u{647} \u{62F}\u{631} \u{62D}\u{627}\u{644} \u{628}\u{627}\u{631}\u{6AF}\u{630}\u{627}\u{631}\u{6CC} \u{627}\u{633}\u{62A}...<br>\u{6CC}\u{627} \u{622}\u{62F}\u{631}\u{633} \u{631}\u{627} \u{62F}\u{631} \u{641}\u{6CC}\u{644}\u{62F} \u{628}\u{627}\u{644}\u{627} \u{648}\u{627}\u{631}\u{62F} \u{6A9}\u{646}\u{6CC}\u{62F}
      </p>
      <p style="font-size:0.72rem; color:var(--text-muted);">\u{645}\u{62E}\u{62A}\u{635}\u{627}\u{62A} \u{67E}\u{6CC}\u{634}\u{200C}\u{641}\u{631}\u{636}: ${lat}, ${lng}</p>
    </div>`;
    // Still call onSelect with default coords so the rest of the flow works
    onSelect({
        lat: lat,
        lng: lng,
        address: ''
    });
}
async function $09a416bfe8ccd350$var$_reverseGeocode(lat, lng, apiKey) {
    try {
        const res = await fetch(`https://api.neshan.org/v5/reverse?lat=${lat}&lng=${lng}`, {
            headers: {
                'Api-Key': apiKey
            }
        });
        const data = await res.json();
        return data.formatted_address || '';
    } catch (_) {
        return '';
    }
}


/**
 * VIEW SUCCESS — تأیید سفارش و وضعیت زنده
 */ 
function $cd4612bccb9995ee$export$b3890eb0ae9dca99(orderInfo, orderType) {
    if (!orderInfo) return;
    document.getElementById('success-order-id').textContent = orderInfo.id;
    document.getElementById('success-order-total').textContent = (0, $08fa37fff71de28c$export$c86caf1be068fb8d)(orderInfo.payable);
    document.getElementById('success-receiver-name').textContent = (0, $08fa37fff71de28c$export$4cf11838cdc2a8a8)(orderInfo.receiverName);
    $cd4612bccb9995ee$var$_setLastStep(orderType);
    $cd4612bccb9995ee$var$_resetTrackerSteps();
}
function $cd4612bccb9995ee$var$_setLastStep(orderType) {
    const steps = {
        delivery: {
            title: "\u062A\u062D\u0648\u06CC\u0644 \u0628\u0647 \u067E\u06CC\u06A9",
            desc: "\u063A\u0630\u0627 \u062A\u062D\u0648\u06CC\u0644 \u0633\u0641\u06CC\u0631 \u0627\u0631\u0633\u0627\u0644 \u0634\u062F \u0648 \u062F\u0631 \u0645\u0633\u06CC\u0631 \u062E\u0627\u0646\u0647 \u0634\u0645\u0627\u0633\u062A"
        },
        takeaway: {
            title: "\u0622\u0645\u0627\u062F\u0647 \u062A\u062D\u0648\u06CC\u0644 \u062D\u0636\u0648\u0631\u06CC",
            desc: "\u0633\u0641\u0627\u0631\u0634 \u0634\u0645\u0627 \u0622\u0645\u0627\u062F\u0647 \u0627\u0633\u062A\u060C \u0628\u0647 \u067E\u06CC\u0634\u062E\u0648\u0627\u0646 \u0645\u0631\u0627\u062C\u0639\u0647 \u06A9\u0646\u06CC\u062F"
        },
        dinein: {
            title: "\u0633\u0631\u0648 \u0631\u0648\u06CC \u0645\u06CC\u0632",
            desc: "\u063A\u0630\u0627 \u0622\u0645\u0627\u062F\u0647 \u0634\u062F\u0647 \u0648 \u0631\u0648\u06CC \u0645\u06CC\u0632 \u0634\u0645\u0627 \u0633\u0631\u0648 \u0645\u06CC\u200C\u06AF\u0631\u062F\u062F"
        }
    };
    const step = steps[orderType] || steps.delivery;
    document.getElementById('tracker-last-step-title').textContent = step.title;
    document.getElementById('tracker-last-step-desc').textContent = step.desc;
}
function $cd4612bccb9995ee$var$_resetTrackerSteps() {
    [
        'step-received',
        'step-cooking',
        'step-delivering'
    ].forEach((id)=>{
        const el = document.getElementById(id);
        if (el) el.className = 'tracker-step';
    });
    document.getElementById('step-received')?.classList.add('active');
}
function $cd4612bccb9995ee$export$3265e6592608a8ae(submittedAt) {
    const elapsed = Date.now() - submittedAt;
    if (elapsed > 15000) document.getElementById('step-cooking')?.classList.add('active');
    if (elapsed > 35000) document.getElementById('step-delivering')?.classList.add('active');
}


class $1b5c52aa873d9715$export$f9ede2acdbdca2a8 {
    #model;
    #isMenuBuilt = false;
    #otpTimerId = null;
    #trackerTimerId = null;
    #activeAddonProduct = null;
    #statusUnsub = null;
    constructor(model){
        this.#model = model;
    }
    // ─── Bootstrap ────────────────────────────────────────────────────────────
    async init() {
        this.#model.restore();
        // Detect gateway redirect (ZarinPal/IDPay return with query params)
        const hasGatewayParams = /Authority|authority|orderId|id=/.test(window.location.search);
        const hash = window.location.hash.replace("#", "");
        const startPage = hasGatewayParams ? "payment-result" : hash || "menu";
        // Set state without navigation guards on init (restore trusted state)
        this.#model.setState({
            currentPage: startPage
        });
        if (!hash) window.location.hash = startPage;
        // Wire all static event listeners — they call back into presenter methods
        (0, $0cdd358936d3140a$export$8a2a893fc4600b33)({
            onNavigate: (page)=>this.#handleDataNavigate(page),
            onAddonSubmit: ()=>this.#handleAddonSubmit(),
            onAddonClose: ()=>this.#closeAddon(),
            onClearCart: ()=>{
                this.#model.clearCart();
                (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0633\u0628\u062F \u062E\u0631\u06CC\u062F \u062A\u062E\u0644\u06CC\u0647 \u0634\u062F", "info");
            },
            onApplyCoupon: ()=>this.#handleApplyCoupon(),
            onInvoiceSubmit: ()=>this.navigate(this.#model.getState().isAuthenticated ? "checkout" : "otp"),
            onNotesChange: (v)=>this.#model.setState({
                    orderNotes: v
                }),
            onFloatingCartClick: ()=>{
                if (!this.#model.getState().restaurantConfig.isOpen) {
                    (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0631\u0633\u062A\u0648\u0631\u0627\u0646 \u0647\u0645\u200C\u0627\u06A9\u0646\u0648\u0646 \u0628\u0633\u062A\u0647 \u0627\u0633\u062A.", "error");
                    return;
                }
                this.navigate("invoice");
            },
            onSendOtp: ()=>this.#handleSendOtp(),
            onVerifyOtp: ()=>this.#handleVerifyOtp(),
            onResendOtp: ()=>this.#handleResendOtp(),
            onChangePhone: ()=>{
                clearInterval(this.#otpTimerId);
                this.#model.setState({
                    otpPhone: null,
                    otpCodeSentAt: null
                });
            },
            onCheckoutSubmit: ()=>this.#handleSubmitOrder(),
            onLogout: ()=>{
                this.#model.logout();
                (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0628\u0627 \u0645\u0648\u0641\u0642\u06CC\u062A \u062E\u0627\u0631\u062C \u0634\u062F\u06CC\u062F", "info");
                this.navigate("menu");
            },
            onRetryPayment: ()=>this.navigate("checkout"),
            onBackToMenu: ()=>this.#resetToMenu()
        });
        // Subscribe to model → re-render
        this.#model.subscribe((state)=>this.#onStateChange(state));
        // Expose globals for HTML onclick="" attributes
        window.appNavigate = (page)=>this.navigate(page);
        window.resetFlowToMenu = ()=>this.#resetToMenu();
        // Browser back/forward
        window.addEventListener("hashchange", ()=>{
            const h = window.location.hash.replace("#", "");
            const curr = this.#model.getState().currentPage;
            if (!h || h === curr) return;
            // Block back-navigation into locked pages
            if (curr === "success") {
                window.location.hash = "menu";
                this.#resetToMenu();
                return;
            }
            this.navigate(h);
        });
        // Initial render
        this.#onStateChange(this.#model.getState());
        // Fetch menu & start real-time status subscription
        await this.#loadMenu();
        this.#subscribeToStatus();
    }
    // ─── data-navigate handler ────────────────────────────────────────────────
    // Called for every element with data-navigate="page" attribute.
    // Some pages need special handling before navigating.
    #handleDataNavigate(page) {
        const state = this.#model.getState();
        // دکمه برگشت در صفحه OTP
        if (page === "otp" && state.currentPage === "otp") {
            // اگه در مرحله کد هستیم، برگرد به مرحله شماره
            if (state.otpPhone && state.otpCodeSentAt) {
                clearInterval(this.#otpTimerId);
                this.#model.setState({
                    otpPhone: null,
                    otpCodeSentAt: null
                });
                return;
            }
            // اگه در مرحله شماره هستیم، برگرد به invoice
            this.navigate("invoice");
            return;
        }
        this.navigate(page);
    }
    // ─── Menu ─────────────────────────────────────────────────────────────────
    async #loadMenu() {
        this.#model.setState({
            menuLoading: true,
            menuError: null
        });
        $7a002140c5914afe$export$429ee859e6a27934();
        try {
            const data = await $73936c209f170d43$export$49ee515c521055ac();
            const patch = {
                menuData: data,
                menuLoading: false
            };
            if (data.restaurantConfig) patch.restaurantConfig = data.restaurantConfig;
            this.#model.setState(patch);
            this.#isMenuBuilt = false;
            this.#renderMenu(this.#model.getState());
        } catch (err) {
            console.error("[Presenter] fetchMenu failed:", err);
            this.#model.setState({
                menuLoading: false,
                menuError: err.message
            });
            $7a002140c5914afe$export$f18becb2f09f38ed(err.message || "\u062E\u0637\u0627 \u062F\u0631 \u062F\u0631\u06CC\u0627\u0641\u062A \u0645\u0646\u0648.", ()=>this.#loadMenu());
        }
    }
    #subscribeToStatus() {
        this.#statusUnsub?.();
        this.#statusUnsub = $73936c209f170d43$export$f2449340a296d5b0((update)=>{
            if (update.type === "STATUS") this.#model.updateRestaurantStatus(update.isOpen);
            else if (update.type === "PRODUCT") this.#model.updateProductAvailability(update.id, update.available);
        });
    }
    // ─── State Router ─────────────────────────────────────────────────────────
    #onStateChange(state) {
        (0, $0cdd358936d3140a$export$f808c2be2eb32cc5)(state.currentPage);
        switch(state.currentPage){
            case "menu":
                this.#renderMenu(state);
                break;
            case "invoice":
                this.#renderInvoice(state);
                break;
            case "otp":
                this.#renderOtp(state);
                break;
            case "checkout":
                this.#renderCheckout(state);
                break;
            case "success":
                this.#renderSuccess(state);
                break;
            case "payment-result":
                this.#renderPaymentResult();
                break;
            case "payment-failed":
                this.#renderPaymentFailed(state);
                break;
        }
    }
    // ─── Page Renderers ───────────────────────────────────────────────────────
    #renderMenu(state) {
        if (state.menuLoading || state.menuError || !state.menuData) return;
        $7a002140c5914afe$export$70f5b6c4f2bee4f1(state.restaurantConfig);
        const categories = this.#model.getCategories();
        const products = this.#model.getProducts();
        if (!this.#isMenuBuilt && categories.length > 0) {
            $7a002140c5914afe$export$8807aa86dc1a7379(categories, products, {
                onTabClick: (id)=>{
                    $7a002140c5914afe$export$6ab60f5372d5a993(id);
                    $7a002140c5914afe$export$ba0a7bf33eb6f76(id);
                },
                onAddClick: (prod)=>this.#onProductAddClick(prod),
                onRemoveClick: (id)=>this.#model.removeLastConfigOfProduct(id),
                onConfigAdd: (id, addons)=>this.#safeCart(()=>this.#model.addToCart(id, addons)),
                onConfigRemove: (id, addons)=>this.#model.removeFromCart(id, addons)
            });
            this.#setupIntersectionObserver();
            this.#isMenuBuilt = true;
        }
        products.forEach((prod)=>{
            $7a002140c5914afe$export$c036cf6106c1fc5b(prod, this.#model.getProductTotalCount(prod.id), state.cart.filter((i)=>i.productId === prod.id), state.restaurantConfig.isOpen, {
                onAddClick: (p)=>this.#onProductAddClick(p),
                onRemoveClick: (id)=>this.#model.removeLastConfigOfProduct(id),
                onConfigAdd: (id, addons)=>this.#safeCart(()=>this.#model.addToCart(id, addons)),
                onConfigRemove: (id, addons)=>this.#model.removeFromCart(id, addons)
            });
        });
        const totalCount = state.cart.reduce((s, i)=>s + i.quantity, 0);
        const totalAmount = state.cart.reduce((s, i)=>s + i.linePrice * i.quantity, 0);
        $7a002140c5914afe$export$b75578d5e5634372(totalCount, totalAmount);
    }
    #renderInvoice(state) {
        if (state.appliedCoupon && this.#model.computeSubtotal() < state.appliedCoupon.minOrder) {
            this.#model.setState({
                appliedCoupon: null
            });
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u06A9\u062F \u062A\u062E\u0641\u06CC\u0641 \u0628\u0647 \u062F\u0644\u06CC\u0644 \u06A9\u0627\u0647\u0634 \u0633\u0628\u062F \u0644\u063A\u0648 \u0634\u062F", "error");
            return;
        }
        $709694b8ee5edd76$export$b3890eb0ae9dca99(state.cart, this.#model.getProducts(), this.#model.computeFinancials(state.orderType), state.orderNotes, {
            onItemAdd: (id, addons)=>this.#safeCart(()=>this.#model.addToCart(id, addons)),
            onItemRemove: (id, addons)=>this.#model.removeFromCart(id, addons)
        });
    }
    #renderOtp(state) {
        clearInterval(this.#otpTimerId);
        // const btnBack = document.getElementById("btn-otp-back");
        if (state.otpPhone && state.otpCodeSentAt && !state.otpVerified) {
            $e3b051dd7ebf4358$export$6bc9a3e15068d5a4(state.otpPhone);
            this.#otpTimerId = $e3b051dd7ebf4358$export$1ad018ea9ed1d4c7(state.otpCodeSentAt, ()=>{});
        // if (btnBack)
        //   btnBack.onclick = () =>
        //     this.#model.setState({ otpPhone: null, otpCodeSentAt: null });
        } else $e3b051dd7ebf4358$export$afa434b436311169();
    }
    #renderCheckout(state) {
        const financials = this.#model.computeFinancials(state.orderType);
        $09a416bfe8ccd350$export$b3890eb0ae9dca99(state, financials, {
            onOrderTypeChange: (type)=>this.#model.setState({
                    orderType: type
                })
        });
        // Init map picker once per checkout render
        $09a416bfe8ccd350$export$d8f76009e91cfb87(state.selectedLocation, (loc)=>{
            this.#model.setState({
                selectedLocation: loc
            });
            (0, $0cdd358936d3140a$export$dead20abe39d024c)(loc.address);
            const addrInput = document.getElementById("receiver-address");
            if (addrInput && loc.address && !addrInput.value) addrInput.value = loc.address;
        });
    }
    #renderSuccess(state) {
        if (!state.lastSubmittedOrder) {
            this.navigate("menu");
            return;
        }
        $cd4612bccb9995ee$export$b3890eb0ae9dca99(state.lastSubmittedOrder, state.orderType);
        clearInterval(this.#trackerTimerId);
        $cd4612bccb9995ee$export$3265e6592608a8ae(state.lastSubmittedOrder.submittedAt);
        this.#trackerTimerId = setInterval(()=>$cd4612bccb9995ee$export$3265e6592608a8ae(state.lastSubmittedOrder.submittedAt), 2000);
    }
    #renderPaymentFailed(state) {
        // page-payment-failed must exist in HTML
        const msgEl = document.getElementById("payment-failed-message");
        if (msgEl) msgEl.textContent = state.paymentFailedReason || "\u067E\u0631\u062F\u0627\u062E\u062A \u0646\u0627\u0645\u0648\u0641\u0642 \u0628\u0648\u062F \u06CC\u0627 \u0644\u063A\u0648 \u0634\u062F.";
        const totalEl = document.getElementById("payment-failed-total");
        if (totalEl && state.pendingOrder) totalEl.textContent = `\u{645}\u{628}\u{644}\u{63A}: ${state.pendingOrder.financials?.grandTotal?.toLocaleString("fa-IR")} \u{62A}\u{648}\u{645}\u{627}\u{646}`;
    }
    async #renderPaymentResult() {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("Status") || params.get("status");
        const orderId = params.get("orderId") || params.get("id");
        const authority = params.get("Authority") || params.get("authority");
        if (status !== "OK" && status !== "ok") {
            this.#model.setState({
                currentPage: "payment-failed",
                paymentFailedReason: "\u067E\u0631\u062F\u0627\u062E\u062A \u062A\u0648\u0633\u0637 \u06A9\u0627\u0631\u0628\u0631 \u0644\u063A\u0648 \u0634\u062F \u06CC\u0627 \u0628\u0627 \u062E\u0637\u0627 \u0645\u0648\u0627\u062C\u0647 \u0634\u062F."
            });
            window.history.replaceState({}, "", `${window.location.pathname}#payment-failed`);
            return;
        }
        // Show loading state on success page while verifying
        (0, $0cdd358936d3140a$export$f808c2be2eb32cc5)("success");
        document.getElementById("success-order-id").textContent = "...";
        try {
            const res = await $73936c209f170d43$export$dcd88816af569b61({
                orderId: orderId,
                authority: authority
            });
            const s = this.#model.getState();
            this.#model.setState({
                lastSubmittedOrder: {
                    id: res.orderId || orderId,
                    payable: s.pendingOrder?.financials?.grandTotal || 0,
                    receiverName: s.receiverName,
                    estimatedMinutes: res.estimatedMinutes || 45,
                    submittedAt: Date.now()
                },
                cart: [],
                appliedCoupon: null,
                couponCode: "",
                orderNotes: "",
                pendingOrder: null,
                currentPage: "success"
            });
            window.history.replaceState({}, "", `${window.location.pathname}#success`);
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u067E\u0631\u062F\u0627\u062E\u062A \u0628\u0627 \u0645\u0648\u0641\u0642\u06CC\u062A \u0627\u0646\u062C\u0627\u0645 \u0634\u062F", "success");
        } catch (err) {
            this.#model.setState({
                currentPage: "payment-failed",
                paymentFailedReason: err.message || "\u062E\u0637\u0627 \u062F\u0631 \u062A\u0623\u06CC\u06CC\u062F \u067E\u0631\u062F\u0627\u062E\u062A"
            });
            window.history.replaceState({}, "", `${window.location.pathname}#payment-failed`);
        }
    }
    // ─── Navigation ───────────────────────────────────────────────────────────
    navigate(page) {
        const guard = this.#model.canNavigateTo(page);
        if (guard) {
            if (guard.reason) (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)(guard.reason, "error");
            if (guard.redirect) {
                // Silently redirect without re-checking guard (avoid infinite loop)
                this.#model.setState({
                    currentPage: guard.redirect
                });
                window.location.hash = guard.redirect;
            }
            return;
        }
        this.#model.setState({
            currentPage: page
        });
        window.location.hash = page;
    }
    // ─── Product Add ──────────────────────────────────────────────────────────
    #onProductAddClick(prod) {
        if (prod.afzoodanis?.length > 0) {
            this.#activeAddonProduct = prod;
            $7a002140c5914afe$export$80acc5193cd9ab1(prod, (selectedAddons)=>{
                $7a002140c5914afe$export$c0e4d383376a4a24(this.#model.calculateLinePrice(prod, selectedAddons));
            });
        } else this.#safeCart(()=>this.#model.addToCart(prod.id));
    }
    #handleAddonSubmit() {
        if (!this.#activeAddonProduct) return;
        this.#safeCart(()=>this.#model.addToCart(this.#activeAddonProduct.id, $7a002140c5914afe$export$d904cc079f49912f()));
        this.#closeAddon();
    }
    #closeAddon() {
        $7a002140c5914afe$export$bc7916b895868493();
        this.#activeAddonProduct = null;
    }
    #safeCart(fn) {
        try {
            fn();
        } catch (err) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)(err.message, "error");
        }
    }
    // ─── Intersection Observer ────────────────────────────────────────────────
    #setupIntersectionObserver() {
        const observer = new IntersectionObserver((entries)=>{
            entries.forEach((e)=>{
                if (e.isIntersecting) $7a002140c5914afe$export$6ab60f5372d5a993(e.target.id.replace("section-", ""));
            });
        }, {
            root: null,
            rootMargin: "-80px 0px -50% 0px",
            threshold: 0
        });
        document.querySelectorAll(".menu-category-section").forEach((s)=>observer.observe(s));
    }
    // ─── OTP Handlers ─────────────────────────────────────────────────────────
    async #handleSendOtp() {
        const phone = document.getElementById("phone-input")?.value.trim() || "";
        if (!/^09\d{9}$/.test(phone)) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0644\u0637\u0641\u0627 \u0634\u0645\u0627\u0631\u0647 \u0647\u0645\u0631\u0627\u0647 \u0645\u0639\u062A\u0628\u0631 \u06F1\u06F1 \u0631\u0642\u0645\u06CC \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F", "error");
            return;
        }
        (0, $0cdd358936d3140a$export$79caa320eed3dcc3)("btn-send-otp", true);
        try {
            await $73936c209f170d43$export$477887879f18714(phone);
            this.#model.setState({
                otpPhone: phone,
                otpCodeSentAt: Date.now()
            });
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u06A9\u062F \u062A\u0627\u06CC\u06CC\u062F (\u06F1\u06F2\u06F3\u06F4) \u0627\u0631\u0633\u0627\u0644 \u0634\u062F", "success");
        } catch (err) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)(err.message, "error");
        } finally{
            (0, $0cdd358936d3140a$export$79caa320eed3dcc3)("btn-send-otp", false, `<span>\u{627}\u{631}\u{633}\u{627}\u{644} \u{6A9}\u{62F} \u{62A}\u{627}\u{6CC}\u{6CC}\u{62F}</span> <i class="fa-solid fa-paper-plane"></i>`);
        }
    }
    async #handleVerifyOtp() {
        const code = $e3b051dd7ebf4358$export$9a0a9c75ccef13fb();
        if (code.length < 4) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0644\u0637\u0641\u0627 \u06A9\u062F \u06F4 \u0631\u0642\u0645\u06CC \u0631\u0627 \u06A9\u0627\u0645\u0644 \u06A9\u0646\u06CC\u062F", "error");
            return;
        }
        (0, $0cdd358936d3140a$export$79caa320eed3dcc3)("btn-verify-otp", true);
        try {
            const res = await $73936c209f170d43$export$5a44d9712f68b0dd(this.#model.getState().otpPhone, code);
            const verifiedPhone = this.#model.getState().otpPhone;
            (0, $73936c209f170d43$export$894225851aaae84a)(res.token);
            this.#model.setState({
                otpVerified: true,
                isAuthenticated: true,
                authToken: res.token || null,
                authenticatedPhone: verifiedPhone,
                receiverPhone: this.#model.getState().receiverPhone || verifiedPhone,
                otpPhone: null,
                otpCodeSentAt: null
            });
            $e3b051dd7ebf4358$export$7351e904ef366a5();
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0648\u0631\u0648\u062F \u0645\u0648\u0641\u0642", "success");
            this.navigate("checkout");
        } catch (err) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)(err.message, "error");
        } finally{
            (0, $0cdd358936d3140a$export$79caa320eed3dcc3)("btn-verify-otp", false, `<span>\u{62A}\u{627}\u{6CC}\u{6CC}\u{62F} \u{648} \u{648}\u{631}\u{648}\u{62F}</span> <i class="fa-solid fa-check"></i>`);
        }
    }
    async #handleResendOtp() {
        const btn = document.getElementById("btn-resend-otp");
        if (btn) btn.disabled = true;
        try {
            await $73936c209f170d43$export$477887879f18714(this.#model.getState().otpPhone);
            this.#model.setState({
                otpCodeSentAt: Date.now()
            });
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u06A9\u062F \u062C\u062F\u06CC\u062F \u0627\u0631\u0633\u0627\u0644 \u0634\u062F", "success");
        } catch (err) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)(err.message, "error");
        } finally{
            if (btn) btn.disabled = false;
        }
    }
    // ─── Coupon ───────────────────────────────────────────────────────────────
    async #handleApplyCoupon() {
        const code = document.getElementById("coupon-input")?.value || "";
        try {
            await this.#model.applyCoupon(code);
            $709694b8ee5edd76$export$d0e7b6cbe1ae6c1e("\u06A9\u062F \u062A\u062E\u0641\u06CC\u0641 \u0627\u0639\u0645\u0627\u0644 \u0634\u062F", true);
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u06A9\u062F \u062A\u062E\u0641\u06CC\u0641 \u062B\u0628\u062A \u0634\u062F", "success");
        } catch (err) {
            $709694b8ee5edd76$export$d0e7b6cbe1ae6c1e(err.message, false);
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)(err.message, "error");
        }
    }
    // ─── Checkout / Order Submit ──────────────────────────────────────────────
    async #handleSubmitOrder() {
        const { name: name, phone: phone, address: address } = $09a416bfe8ccd350$export$e992d02417fcc76c();
        const state = this.#model.getState();
        const financials = this.#model.computeFinancials(state.orderType);
        if (!name) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0644\u0637\u0641\u0627 \u0646\u0627\u0645 \u062A\u062D\u0648\u06CC\u0644\u200C\u06AF\u06CC\u0631\u0646\u062F\u0647 \u0631\u0627 \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F", "error");
            return;
        }
        if (!/^09\d{9}$/.test(phone)) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0644\u0637\u0641\u0627 \u0634\u0645\u0627\u0631\u0647 \u062A\u0645\u0627\u0633 \u0645\u0639\u062A\u0628\u0631\u06CC \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F", "error");
            return;
        }
        if (state.orderType === "delivery" && !address) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0644\u0637\u0641\u0627 \u0622\u062F\u0631\u0633 \u062A\u062D\u0648\u06CC\u0644 \u0631\u0627 \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F", "error");
            return;
        }
        // Full order payload — includes everything backend needs
        const payload = {
            clientPhone: state.authenticatedPhone,
            receiver: {
                name: name,
                phone: phone
            },
            orderType: state.orderType,
            address: state.orderType === "delivery" ? address : "\u062A\u062D\u0648\u06CC\u0644 \u062D\u0636\u0648\u0631\u06CC \u062F\u0631 \u0645\u062D\u0644 \u0631\u0633\u062A\u0648\u0631\u0627\u0646",
            location: state.orderType === "delivery" ? state.selectedLocation || null : null,
            items: state.cart.map(({ productId: productId, quantity: quantity, selectedAddons: selectedAddons, linePrice: linePrice })=>({
                    productId: productId,
                    quantity: quantity,
                    selectedAddons: selectedAddons,
                    linePrice: linePrice
                })),
            orderNotes: state.orderNotes,
            couponCode: state.couponCode,
            financials: financials
        };
        (0, $0cdd358936d3140a$export$79caa320eed3dcc3)("btn-checkout-submit", true);
        try {
            const res = await $73936c209f170d43$export$b1a61f48eea455bf(payload);
            if (res.paymentUrl) {
                // Save full order payload so we can access it after gateway callback
                this.#model.setState({
                    pendingOrder: payload
                });
                window.location.href = res.paymentUrl;
                return;
            }
            // No gateway redirect — order completed directly
            this.#model.setState({
                receiverName: name,
                receiverPhone: phone,
                receiverAddress: state.orderType === "delivery" ? address : "",
                lastSubmittedOrder: {
                    id: res.orderId,
                    payable: financials.grandTotal,
                    receiverName: name,
                    estimatedMinutes: res.estimatedMinutes,
                    submittedAt: Date.now()
                },
                cart: [],
                appliedCoupon: null,
                couponCode: "",
                orderNotes: "",
                pendingOrder: null
            });
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)("\u0633\u0641\u0627\u0631\u0634 \u0628\u0627 \u0645\u0648\u0641\u0642\u06CC\u062A \u062B\u0628\u062A \u0634\u062F", "success");
            this.navigate("success");
        } catch (err) {
            (0, $0cdd358936d3140a$export$f6cef6fcf9ca4e17)(err.message || "\u062E\u0637\u0627 \u062F\u0631 \u062B\u0628\u062A \u0633\u0641\u0627\u0631\u0634", "error");
        } finally{
            (0, $0cdd358936d3140a$export$79caa320eed3dcc3)("btn-checkout-submit", false, `<span>\u{67E}\u{631}\u{62F}\u{627}\u{62E}\u{62A} \u{648} \u{62B}\u{628}\u{62A} \u{646}\u{647}\u{627}\u{6CC}\u{6CC}</span> <i class="fa-solid fa-credit-card"></i>`);
        }
    }
    // ─── Reset ────────────────────────────────────────────────────────────────
    #resetToMenu() {
        clearInterval(this.#trackerTimerId);
        this.#model.setState({
            lastSubmittedOrder: null,
            paymentFailedReason: null
        });
        this.navigate("menu");
    }
}


window.addEventListener('DOMContentLoaded', async ()=>{
    const model = new (0, $f381a3c3b9cf5311$export$75ea5ef3820854ae)();
    const presenter = new (0, $1b5c52aa873d9715$export$f9ede2acdbdca2a8)(model);
    await presenter.init();
});


//# sourceMappingURL=frontend.6421fbb1.js.map
